import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { User, Mail, Calendar, Heart, Clock, ShieldCheck, Award, Building2, Wine as WineIcon, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import WineCard from "@/components/WineCard";
import SubmitWineDialog from "@/components/SubmitWineDialog";
import { useAuth } from "@/context/AuthContext";

import { API } from "@/lib/apiBase";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}

const ROLE_LABEL = {
  admin: "Administrator",
  sommelier: "Sommelier",
  partner: "Partner",
  public: "Public",
};

const ROLE_ICON = {
  admin: ShieldCheck,
  sommelier: Award,
  partner: Building2,
  public: User,
};

export default function Profile() {
  const { user, loading: authLoading } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !user.approved) {
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/favorites`)
      .then((r) => setFavorites(r.data.wines || []))
      .catch((err) => console.error("Failed to load favorites", err))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (authLoading) {
    return (
      <div className="pt-24 pb-20 min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-white/30 font-body">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  const RoleIcon = ROLE_ICON[user.role] || User;

  return (
    <main className="pt-24 pb-20 min-h-screen" data-testid="profile-page">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="mb-12">
          <p className="font-accent text-xs tracking-[0.3em] text-gold/60 mb-2">ACCOUNT</p>
          <h1 className="font-display text-3xl sm:text-4xl text-white/90">Profile</h1>
        </div>

        {!user.approved && (
          <div className="glass-card rounded-sm p-5 mb-10 flex items-start gap-4 border-l-2 border-gold/40" data-testid="pending-approval-banner">
            <Clock className="w-5 h-5 text-gold shrink-0 mt-0.5" />
            <div>
              <p className="font-display text-lg text-white/90 mb-1">Awaiting admin approval</p>
              <p className="font-body text-sm text-white/50">
                Your {ROLE_LABEL[user.role]} account was created successfully. You can browse the catalog now,
                but features like favorites{user.role === "sommelier" ? ", submitting credits" : user.role === "partner" ? ", submitting wines" : ""} will unlock once an admin approves your registration.
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="profile-info">
          <InfoCard icon={User} label="Name" value={user.name} testId="profile-name" />
          <InfoCard icon={Mail} label="Email" value={user.email} testId="profile-email" />
          <InfoCard icon={Calendar} label="Member Since" value={formatDate(user.created_at)} testId="profile-member-since" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-14">
          <div className="glass-card rounded-sm p-5 flex items-start gap-3" data-testid="profile-role-card">
            <RoleIcon className="w-4 h-4 text-merlot mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-body text-[10px] tracking-wider text-white/30 uppercase mb-0.5">Role</p>
              <div className="flex items-center gap-2">
                <p className="font-body text-sm text-white/80" data-testid="profile-role">{ROLE_LABEL[user.role] || user.role}</p>
                {user.approved ? (
                  <Badge variant="outline" className="text-[10px] border-emerald-400/40 text-emerald-400" data-testid="profile-approved-badge">APPROVED</Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px] border-gold/40 text-gold" data-testid="profile-pending-badge">PENDING</Badge>
                )}
              </div>
            </div>
          </div>
          {user.credential && (
            <InfoCard icon={Award} label="Credential" value={user.credential} testId="profile-credential" />
          )}
          {user.business_name && (
            <InfoCard icon={Building2} label="Business" value={user.business_name} testId="profile-business" />
          )}
          {user.membership?.active && (
            <div className="glass-card rounded-sm p-5 flex items-start gap-3 border-l-2 border-gold/50" data-testid="profile-membership-card">
              <Sparkles className="w-4 h-4 text-gold mt-0.5 shrink-0" />
              <div>
                <p className="font-body text-[10px] tracking-wider text-white/30 uppercase mb-0.5">Membership</p>
                <p className="font-body text-sm text-white/80 capitalize" data-testid="profile-membership">
                  {user.membership.plan} · {user.membership.interval}
                </p>
                {user.membership.expires_at && (
                  <p className="font-body text-[10px] text-white/40 mt-1">
                    Renews {new Date(user.membership.expires_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Role-specific actions */}
        <div className="mb-10 flex flex-wrap items-center gap-3" data-testid="profile-actions">
          {(user.role === "partner" || user.role === "admin") && (
            <SubmitWineDialog
              disabled={!user.approved}
              trigger={
                <Button disabled={!user.approved} data-testid="profile-submit-wine" className="bg-merlot hover:bg-merlot-light text-white gap-2 disabled:opacity-40">
                  <WineIcon className="w-4 h-4" /> Submit a Wine
                </Button>
              }
            />
          )}
          {user.role === "sommelier" && (
            <Link to="/catalog">
              <Button disabled={!user.approved} data-testid="profile-credit-cta" className="bg-merlot hover:bg-merlot-light text-white gap-2 disabled:opacity-40">
                <Award className="w-4 h-4" /> Credit Wines in Catalog
              </Button>
            </Link>
          )}
        </div>

        <Separator className="bg-white/5 mb-10" />

        <div className="mb-6 flex items-center gap-3">
          <Heart className="w-4 h-4 text-merlot-light fill-current" />
          <p className="font-accent text-xs tracking-[0.3em] text-gold/60">FAVORITES</p>
          <span className="font-body text-xs text-white/30">({favorites.length})</span>
        </div>

        {!user.approved ? (
          <div className="text-center py-16 glass-card rounded-sm" data-testid="favorites-locked">
            <Clock className="w-8 h-8 text-white/15 mx-auto mb-4" />
            <p className="font-display text-xl text-white/40 mb-2">Favorites unlock after approval</p>
            <p className="font-body text-sm text-white/25">An admin will review your account shortly.</p>
          </div>
        ) : loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={`fav-skeleton-${i}`} className="glass-card rounded-sm p-5 h-52 animate-pulse" />
            ))}
          </div>
        ) : favorites.length === 0 ? (
          <div className="text-center py-16 glass-card rounded-sm" data-testid="no-favorites">
            <Heart className="w-8 h-8 text-white/15 mx-auto mb-4" />
            <p className="font-display text-xl text-white/40 mb-2">No favorites yet</p>
            <p className="font-body text-sm text-white/25">Tap the heart on any wine to save it here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="favorites-grid">
            {favorites.map((wine) => <WineCard key={wine.id} wine={wine} />)}
          </div>
        )}
      </div>
    </main>
  );
}

function InfoCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="glass-card rounded-sm p-5 flex items-start gap-3">
      <Icon className="w-4 h-4 text-merlot mt-0.5 shrink-0" />
      <div>
        <p className="font-body text-[10px] tracking-wider text-white/30 uppercase mb-0.5">{label}</p>
        <p className="font-body text-sm text-white/80" data-testid={testId}>{value}</p>
      </div>
    </div>
  );
}
